import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import { SHARED_ARTISTS_KEY } from "@/utils/artistsStore";
import { defaultMeetMakers, defaultArtists, type MeetMakersData, type ArtistEntry } from "@/data/siteContent";

interface MeetOurMakersSectionProps { dataKey?: string; }

export default function MeetOurMakersSection({ dataKey }: MeetOurMakersSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const navigate = useNavigate();

  // Section header data (eyebrow, heading, etc.)
  const [sectionData, setSectionData] = useAdminStorage<MeetMakersData>(
    dataKey ?? "ma_meet_makers_header",
    defaultMeetMakers
  );

  // Artists — shared with the Artists page
  const [artists, setArtists] = useAdminStorage<ArtistEntry[]>(
    SHARED_ARTISTS_KEY,
    defaultArtists
  );

  const setSection = useCallback(<K extends keyof MeetMakersData>(key: K, val: MeetMakersData[K]) => {
    setSectionData({ ...sectionData, [key]: val });
  }, [sectionData, setSectionData]);

  const updateArtist = useCallback((id: string, field: keyof ArtistEntry, val: string | boolean) => {
    setArtists(artists.map((a) => a.id === id ? { ...a, [field]: val } : a));
  }, [artists, setArtists]);

  // Only show the first 6 artists on the homepage
  const displayArtists = artists.slice(0, 6);

  const bg = isDark ? "bg-gray-900" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";

  return (
    <section id="meet-makers" className={`py-24 px-6 transition-colors duration-300 ${bg}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          {(isAdmin || sectionData.eyebrow) && (
            isAdmin ? (
              <AdminEditField value={sectionData.eyebrow} onSave={(v) => setSection("eyebrow", v)} onDelete={() => setSection("eyebrow", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{sectionData.eyebrow}</span>
              </AdminEditField>
            ) : (
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{sectionData.eyebrow}</span>
            )
          )}
          {(isAdmin || sectionData.heading) && (
            isAdmin ? (
              <AdminEditField value={sectionData.heading} onSave={(v) => setSection("heading", v)} onDelete={() => setSection("heading", "")} className="mt-3 mb-4" inputClassName="text-4xl font-extrabold text-center">
                <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{sectionData.heading}</h2>
              </AdminEditField>
            ) : (
              <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{sectionData.heading}</h2>
            )
          )}
          {(isAdmin || sectionData.subheading) && (
            isAdmin ? (
              <AdminEditField value={sectionData.subheading} onSave={(v) => setSection("subheading", v)} onDelete={() => setSection("subheading", "")} multiline inputClassName="text-lg text-center">
                <p className={`text-lg max-w-2xl mx-auto ${subColor}`}>{sectionData.subheading}</p>
              </AdminEditField>
            ) : (
              <p className={`text-lg max-w-2xl mx-auto ${subColor}`}>{sectionData.subheading}</p>
            )
          )}
        </div>

        {/* Makers grid — first 6 artists */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 mb-12">
          {displayArtists.map((artist) => (
            <div
              key={artist.id}
              className={`rounded-2xl border overflow-hidden transition-all hover:-translate-y-1 cursor-pointer group ${cardBg}`}
              onClick={() => {
                if (!isAdmin) navigate("/artists");
              }}
            >
              {/* Square image */}
              <div className="w-full aspect-square overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {isAdmin ? (
                  <AdminImageUpload
                    src={artist.image}
                    alt={artist.name}
                    onReplace={(url) => updateArtist(artist.id, "image", url)}
                    objectPosition={artist.objectPosition ?? "50% 50%"}
                    onPositionChange={(pos) => updateArtist(artist.id, "objectPosition", pos)}
                    mobileObjectPosition={artist.mobileObjectPosition ?? artist.objectPosition ?? "50% 50%"}
                    onMobilePositionChange={(pos) => updateArtist(artist.id, "mobileObjectPosition", pos)}

                    className="w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    isDark={isDark}
                  />
                ) : (
                  artist.image ? (
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ objectPosition: artist.objectPosition ?? "50% 50%" }}
                    />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
                      <i className="ri-image-line text-3xl text-pink-300"></i>
                    </div>
                  )
                )}
              </div>
              {/* Info */}
              <div className="p-3" onClick={(e) => e.stopPropagation()}>
                {isAdmin ? (
                  <>
                    <AdminEditField value={artist.name} onSave={(v) => updateArtist(artist.id, "name", v)} inputClassName="font-bold text-sm">
                      <div className={`font-bold text-sm leading-tight ${headingColor}`}>{artist.name}</div>
                    </AdminEditField>
                    <AdminEditField value={artist.medium} onSave={(v) => updateArtist(artist.id, "medium", v)} className="mt-1" inputClassName="text-xs">
                      <div className={`text-xs mt-1 ${subColor}`}>{artist.medium}</div>
                    </AdminEditField>
                  </>
                ) : (
                  <>
                    <div className={`font-bold text-sm leading-tight ${headingColor}`}>{artist.name}</div>
                    <div className={`text-xs mt-1 ${subColor}`}>{artist.medium}</div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* See More CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate("/artists")}
            className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3.5 rounded-full transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            {isAdmin ? (
              <AdminEditField value={sectionData.seeMoreLabel} onSave={(v) => setSection("seeMoreLabel", v)} inputClassName="text-white font-bold text-center">
                <span>{sectionData.seeMoreLabel}</span>
              </AdminEditField>
            ) : (
              <span>{sectionData.seeMoreLabel}</span>
            )}
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
